import { useTranslation } from 'react-i18next';
import styles from './RulesScreen.module.css';

/**
 * The rules explanation for a custom bolão — a competition made of
 * organizer-authored questions rather than a fixed sport's fixture list.
 *
 * Unlike `RulesScreen`'s football content, none of this is computed: a
 * custom bolão has no single ruleset to resolve worked examples from — its
 * organizer can mix question types freely, each with its own point range or
 * none at all. So this is a static explanation of the options that exist and
 * how each one turns into points, reusing football rules' visual language
 * (`RulesScreen.module.css`) for a consistent look rather than the numbers.
 */
export default function CustomRulesScreen() {
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.hero}>
        <span className={styles.heroIcon}>📝</span>
        <h1 className={styles.heroTitle}>{t('rules.custom.heroTitle')}</h1>
        <p className={styles.heroSubtitle}>{t('rules.custom.heroSubtitle')}</p>
      </div>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>❓</span>
          <h2>{t('rules.custom.typesSection.title')}</h2>
        </div>
        <p className={styles.body}>{t('rules.custom.typesSection.body')}</p>

        <div className={styles.rulesList}>
          <div className={styles.rule}>
            <span className={styles.ruleIcon}>📋</span>
            <span>{t('rules.custom.typesSection.multipleChoice')}</span>
          </div>
          <div className={styles.rule}>
            <span className={styles.ruleIcon}>⚽</span>
            <span>{t('rules.custom.typesSection.football')}</span>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>🎯</span>
          <h2>{t('rules.custom.perAnswerSection.title')}</h2>
          <span className={styles.sectionBadge}>{t('rules.custom.perAnswerSection.badge')}</span>
        </div>
        <p className={styles.body}>{t('rules.custom.perAnswerSection.body')}</p>

        <div className={`${styles.callout} ${styles.calloutInfo}`}>
          <p>{t('rules.custom.perAnswerSection.calloutRange')}</p>
          <p>{t('rules.custom.perAnswerSection.calloutSum')}</p>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>🤝</span>
          <h2>{t('rules.custom.winnerLoserSection.title')}</h2>
        </div>
        <p className={styles.body}>{t('rules.custom.winnerLoserSection.body')}</p>

        <div className={`${styles.callout} ${styles.calloutDimmed}`}>
          <p>{t('rules.custom.winnerLoserSection.calloutNoPoints')}</p>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>⚽</span>
          <h2>{t('rules.custom.footballSection.title')}</h2>
        </div>
        <p className={styles.body}>{t('rules.custom.footballSection.body')}</p>

        <div className={styles.rulesList}>
          <div className={styles.rule}>
            <span className={styles.ruleIcon}>✅</span>
            <span>{t('rules.custom.footballSection.ruleOutcome')}</span>
          </div>
          <div className={styles.rule}>
            <span className={styles.ruleIcon}>🎯</span>
            <span>{t('rules.custom.footballSection.ruleExact')}</span>
          </div>
          <div className={styles.rule}>
            <span className={styles.ruleIcon}>🍕</span>
            <span>{t('rules.custom.footballSection.ruleProximity')}</span>
          </div>
        </div>

        <p className={styles.bodyNote}>{t('rules.custom.footballSection.note')}</p>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>⏰</span>
          <h2>{t('rules.custom.deadlinesSection.title')}</h2>
        </div>
        <p className={styles.body}>{t('rules.custom.deadlinesSection.body')}</p>

        <div className={styles.rulesList}>
          <div className={styles.rule}>
            <span className={styles.ruleIcon}>📅</span>
            <span>{t('rules.custom.deadlinesSection.ruleDeadline')}</span>
          </div>
          <div className={styles.rule}>
            <span className={styles.ruleIcon}>🔒</span>
            <span>{t('rules.custom.deadlinesSection.ruleLockForever')}</span>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>🏆</span>
          <h2>{t('rules.custom.revealSection.title')}</h2>
        </div>
        <p className={styles.body}>{t('rules.custom.revealSection.body')}</p>

        <div className={`${styles.callout}`}>
          <p>{t('rules.custom.revealSection.calloutLeaderboard')}</p>
          <p className={styles.calloutNote}>
            {t('rules.custom.revealSection.calloutCorrectCount')}
          </p>
        </div>
      </section>

      <p className={styles.footer}>{t('rules.footer')}</p>
    </>
  );
}
